import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Wrench, Tag, Search, Ban, TrendingUp, Smartphone, Sparkles, Activity, ChevronRight, Layers, Store, Globe, Bot, Link2 } from 'lucide-react';
import ProductLabelingTool from './components/ProductLabelingTool';
import KeywordOpportunitiesTool from './components/KeywordOpportunitiesTool';
import KeywordPlannerTool from './components/KeywordPlannerTool';
import NegativeKeywordsTool from './components/NegativeKeywordsTool';
import MicrosoftNegativeKeywordsTool from './components/MicrosoftNegativeKeywordsTool';
import BidOptimizerTool from './components/BidOptimizerTool';
import DeviceBidAdjustmentsTool from './components/DeviceBidAdjustmentsTool';
import FeedDiagnosticsTool from './components/FeedDiagnosticsTool';
import SeaSeoOverlapTool from './components/SeaSeoOverlapTool';
import FeedOptimizerTool from './components/FeedOptimizerTool';
import CompetitorAdWatchTool from './components/CompetitorAdWatchTool';
import SerpTrackingTool from './components/SerpTrackingTool';
import AiVisibilityTool from './components/AiVisibilityTool';
import BacklinksTool from './components/BacklinksTool';
import DiagnosticSeoView from './components/DiagnosticSeoView';
import DiagnosticView from './components/DiagnosticView';
import {
  ORDRE_CATEGORIES, categorieDeLOutil, sousCategorieDeLOutil,
  estUneSousCategorie, categorieParenteDe, grouperParSousCategorie
} from './constants/toolCategories';
import googleAdsLogo from './logos/google-ads.png';
import searchConsoleLogo from './logos/google-search-console.png';
import metaAdsLogo from './logos/meta-ads.png';
import microsoftAdsLogo from './logos/microsoft-ads.png';
import './ToolsView.css';

/**
 * Logos de plateforme, par statut d'outil.
 *
 * Une carte explicite, et non un « sinon Google Ads » : c'est ce repli qui collait le logo Google
 * Ads aux trois outils SEO, lesquels n'interrogent que DataForSEO et n'ont rien à voir avec un
 * compte publicitaire. Un statut absent de cette carte n'affiche aucun logo — rien vaut mieux
 * qu'une plateforme fausse.
 */
const LOGOS_PAR_STATUT = {
  'GOOGLE ADS': [{ src: googleAdsLogo, alt: 'Google Ads' }],
  'GOOGLE ADS + GSC': [{ src: googleAdsLogo, alt: 'Google Ads' }],
  'MERCHANT API': [{ src: googleAdsLogo, alt: 'Google Ads' }],
  'SEARCH CONSOLE': [{ src: searchConsoleLogo, alt: 'Google Search Console' }],
  'MICROSOFT ADS': [{ src: microsoftAdsLogo, alt: 'Microsoft Ads' }],
  'META ADS': [{ src: metaAdsLogo, alt: 'Meta Ads' }],
  'MULTI-PLATEFORME': [
    { src: googleAdsLogo, alt: 'Google Ads' },
    { src: microsoftAdsLogo, alt: 'Microsoft Ads' }
  ]
};

/** Le logo d'un outil, ou rien du tout si son statut n'en désigne aucun. */
const LogoDeLOutil = ({ statut }) => {
  const logos = LOGOS_PAR_STATUT[statut] || [];
  if (logos.length === 0) return null;

  return (
    <div className="tool-card-logo">
      {logos.length > 1 ? (
        <div className="multi-platform-logos">
          {logos.map(logo => <img key={logo.alt} src={logo.src} alt={logo.alt} />)}
        </div>
      ) : (
        <img src={logos[0].src} alt={logos[0].alt} />
      )}
    </div>
  );
};

const ToolsView = ({ token, user, clientTools = [], selectedClientId, userClients = [], setCurrentView, setOpenSearchConsoleOnLibraryLoad, globalConnectors = [] }) => {
  const [selectedTool, setSelectedTool] = useState(null);
  /**
   * Catégorie sur laquelle la page est cadrée, ou `null` pour tout voir.
   *
   * Volontairement hors de l'URL et de l'historique : c'est un filtre d'affichage, pas un
   * endroit où l'on revient. L'y mettre encombrerait le bouton Précédent de pas qui ne mènent
   * nulle part, là où l'ouverture d'un outil, elle, mérite son entrée.
   */
  const [categorieCadree, setCategorieCadree] = useState(null);
  // Initialiser activeTool directement depuis l'URL
  const [activeTool, setActiveTool] = useState(() => {
    const hash = window.location.hash;
    if (hash.startsWith('#tools/')) {
      const hashParts = hash.split('?');
      const toolId = hashParts[0].replace('#tools/', '');
      if (toolId) {
        console.log('[TOOLS] Initialisation state depuis URL, toolId:', toolId);
        return toolId;
      }
    }
    return null;
  });

  /**
   * Les maillons que l'outil ouvert ajoute au fil d'Ariane, après son propre nom.
   *
   * Un outil qui navigue à l'intérieur de lui-même — le Diagnostic, qui descend sur un compte
   * puis sur ses contrôles — n'avait aucun moyen de le dire : le fil s'arrêtait à son nom,
   * puisque c'est `ToolsView` qui le rend et non lui. Il le remonte donc par `onFilChange`, sous
   * la forme `[{ cle, libelle, action }]`, et `renderBreadcrumb` les pose à la suite.
   *
   * Remis à zéro au changement d'outil : un fil laissé là ferait annoncer au suivant une
   * position qui n'est pas la sienne.
   */
  const [filDeLOutil, setFilDeLOutil] = useState([]);

  useEffect(() => { setFilDeLOutil([]); }, [activeTool]);

  // Récupérer le nom du client sélectionné
  const selectedClientName = selectedClientId
    ? userClients.find(c => c.id === selectedClientId)?.name || 'Client'
    : null;

  // Fonction pour changer d'outil avec gestion de l'historique
  const setActiveToolWithHistory = useCallback((toolId, skipHistory = false) => {
    setActiveTool(toolId);
    if (!skipHistory) {
      const clientParam = selectedClientId ? `?client=${selectedClientId}` : '';
      const newHash = toolId ? `#tools/${toolId}${clientParam}` : `#tools${clientParam}`;
      window.history.pushState({ tool: toolId, clientId: selectedClientId }, '', newHash);
    }
  }, [selectedClientId]);

  // Écouter le bouton back/forward du navigateur
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state && event.state.tool !== undefined) {
        setActiveTool(event.state.tool);
      } else {
        // Vérifier le hash pour déterminer l'outil
        const hashParts = window.location.hash.split('?');
        const hash = hashParts[0];
        if (hash.startsWith('#tools/')) {
          const toolId = hash.replace('#tools/', '');
          setActiveTool(toolId);
        } else {
          setActiveTool(null);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Vérifier localStorage comme fallback au montage
  useEffect(() => {
    if (!activeTool) {
      const toolToOpen = localStorage.getItem('kokpeet_openTool');
      if (toolToOpen) {
        setActiveToolWithHistory(toolToOpen);
        localStorage.removeItem('kokpeet_openTool');
      }
    }
  }, []); // Exécuter uniquement au montage

  // Écouter l'événement openTool depuis la sidebar
  useEffect(() => {
    const handleOpenTool = (event) => {
      const { toolId } = event.detail;
      if (toolId) {
        setActiveToolWithHistory(toolId);
      }
    };

    window.addEventListener('openTool', handleOpenTool);
    return () => window.removeEventListener('openTool', handleOpenTool);
  }, [setActiveToolWithHistory]);

  /**
   * Cliquer une catégorie de la barre latérale n'ouvre pas un outil : elle **cadre la page** sur
   * les outils de cette catégorie.
   *
   * Le geste ferme donc l'outil ouvert, s'il y en a un. Rester dessus en changeant la liste
   * derrière laisserait croire à un clic sans effet — la sidebar est justement l'endroit d'où
   * l'on cherche autre chose.
   */
  useEffect(() => {
    const handleOpenCategory = (event) => {
      const { category } = event.detail || {};
      if (!category) return;
      setActiveToolWithHistory(null);
      // Recliquer la catégorie déjà cadrée revient à tout réafficher : le clic reste réversible
      // sans avoir à chercher où l'annuler.
      setCategorieCadree(actuelle => (actuelle === category ? null : category));
    };

    window.addEventListener('openToolCategory', handleOpenCategory);
    return () => window.removeEventListener('openToolCategory', handleOpenCategory);
  }, [setActiveToolWithHistory]);

  // Notifier App.js quand activeTool change (pour mettre à jour la sidebar)
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('openTool', { detail: { toolId: activeTool } }));
  }, [activeTool]);

  // Fonction pour mettre à jour l'URL avec le compte sélectionné
  const updateUrlWithAccount = useCallback((accountId) => {
    const params = new URLSearchParams();
    if (selectedClientId) params.set('client', selectedClientId);
    if (accountId) params.set('account', accountId);
    const queryString = params.toString();
    const newHash = activeTool
      ? `#tools/${activeTool}${queryString ? '?' + queryString : ''}`
      : `#tools${queryString ? '?' + queryString : ''}`;
    window.history.replaceState(
      { tool: activeTool, clientId: selectedClientId, accountId },
      '',
      newHash
    );
  }, [activeTool, selectedClientId]);

  // Initialiser l'URL avec le client au montage ou quand le client change
  useEffect(() => {
    // Récupérer le paramètre account depuis l'URL actuelle pour le conserver
    const currentParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const currentAccountId = currentParams.get('account');

    const params = new URLSearchParams();
    if (selectedClientId) params.set('client', selectedClientId);
    if (currentAccountId) params.set('account', currentAccountId);
    const queryString = params.toString();

    const currentHash = window.location.hash.split('?')[0];
    if (currentHash.startsWith('#tools')) {
      const newHash = activeTool
        ? `#tools/${activeTool}${queryString ? '?' + queryString : ''}`
        : `#tools${queryString ? '?' + queryString : ''}`;
      window.history.replaceState({ tool: activeTool, clientId: selectedClientId, accountId: currentAccountId }, '', newHash);
    }
  }, [selectedClientId, activeTool]);

  const tools = [
    {
      id: 'negative-keywords-hub',
      name: 'Termes de Recherche à Exclure',
      description: 'Identifier les termes de recherche qui gaspillent votre budget',
      icon: <Ban size={24} />,
      status: 'MULTI-PLATEFORME',
      isHub: true,
      features: [
        'Détection des termes sans conversion avec clics élevés',
        'Analyse du taux de conversion par rapport à la moyenne du compte',
        'Priorisation par niveau de gaspillage budgétaire',
        'Calcul du budget gaspillé par terme',
        'Filtres personnalisables (clics minimum, période)'
      ]
    },
    {
      id: 'negative-keywords',
      name: 'Termes de Recherche à Exclure',
      description: 'Identifier les termes de recherche qui gaspillent votre budget',
      icon: <Ban size={24} />,
      status: 'GOOGLE ADS',
      hidden: true, // Masqué de la grille principale, accessible via le hub
      features: [
        'Détection des termes sans conversion avec clics élevés',
        'Analyse du taux de conversion par rapport à la moyenne du compte',
        'Priorisation par niveau de gaspillage budgétaire',
        'Calcul du budget gaspillé par terme',
        'Filtres personnalisables (clics minimum, période)'
      ]
    },
    {
      id: 'microsoft-negative-keywords',
      name: 'Termes de Recherche à Exclure',
      description: 'Identifier les termes de recherche qui gaspillent votre budget Microsoft Ads',
      icon: <Ban size={24} />,
      status: 'MICROSOFT ADS',
      hidden: true, // Masqué de la grille principale, accessible via le hub
      features: [
        'Détection des termes sans conversion avec clics élevés',
        'Analyse du taux de conversion par rapport à la moyenne du compte',
        'Priorisation par niveau de gaspillage budgétaire',
        'Calcul du budget gaspillé par terme',
        'Filtres personnalisables (clics minimum, période)'
      ]
    },
    {
      id: 'keyword-opportunities',
      name: 'Détecteur d\'Opportunités',
      description: 'Détecter de nouveaux mots-clés performants à partir des termes de recherche',
      icon: <Search size={24} />,
      status: 'GOOGLE ADS',
      features: [
        'Analyse des termes de recherche performants (Search & Performance Max)',
        'Vérification automatique des doublons dans les campagnes existantes',
        'Scoring intelligent basé sur CTR, conversions et volume',
        'Recommandations d\'enchères et types de correspondance',
        'Export CSV pour import dans Google Ads Editor'
      ]
    },
    {
      id: 'keyword-planner',
      name: 'Générateur d\'Idées de Mots-Clés',
      description: 'Découvrez de nouvelles opportunités de mots-clés grâce à l\'API Google Ads Keyword Planner',
      icon: <Sparkles size={24} />,
      status: 'GOOGLE ADS',
      features: [
        'Génération d\'idées de mots-clés à partir d\'une URL',
        'Recherche à partir de mots-clés de départ (seed keywords)',
        'Volume de recherche mensuel et tendances',
        'Niveau de concurrence et estimations CPC',
        'Vérification automatique des mots-clés déjà présents dans le compte'
      ]
    },
    {
      id: 'product-labeling',
      name: 'Étiquetage de Produits',
      description: 'Attribuer automatiquement des custom_label aux produits selon leurs performances',
      icon: <Tag size={24} />,
      status: 'GOOGLE ADS',
      features: [
        'Classification automatique basée sur les performances (Top/Middle/Low/Zéro/Zombies)',
        'Seuils personnalisables (ventes et ROAS)',
        'Génération de flux XML pour Merchant Center',
        'Mise à jour programmable (24h par défaut)',
        'Période d\'analyse configurable (15 jours par défaut)'
      ]
    },
    {
      id: 'bid-optimizer',
      name: 'Optimiseur d\'Enchères',
      description: 'Identifier les opportunités d\'ajustement d\'enchères basées sur les performances et l\'Impression Share',
      icon: <TrendingUp size={24} />,
      status: 'GOOGLE ADS',
      features: [
        'Analyse combinée des performances (ROAS, CPA) et de l\'Impression Share',
        'Recommandations d\'augmentation ou de baisse des enchères',
        'Détection des limitations de budget vs. enchères',
        'Granularité par campagne ou groupe d\'annonces',
        'Paramètres personnalisables (ROAS cible, CPA cible, agressivité)'
      ]
    },
    {
      id: 'device-bid-adjustments',
      name: 'Ajusteur d\'Enchères par Appareils',
      description: 'Recommandations d\'ajustement d\'enchères par appareil (Mobile, Desktop, Tablette, TV)',
      icon: <Smartphone size={24} />,
      status: 'GOOGLE ADS',
      features: [
        'Analyse des performances par appareil',
        'Recommandations d\'ajustement en pourcentage (+30%, -50%, etc.)',
        'Support campagne et groupe d\'annonces',
        'Détection automatique des contraintes (stratégies incompatibles)',
        'Affichage des modificateurs actuels (campagne et adgroup)'
      ]
    },
    {
      id: 'feed-diagnostics',
      name: 'Diagnostic du Flux',
      description: 'Tableau de bord de santé du flux Google Shopping en temps réel',
      icon: <Activity size={24} />,
      status: 'MERCHANT API',
      badge: 'NEW',
      features: [
        'Pourcentage de produits approuvés, en attente et rejetés',
        'Score de santé global du flux (A à F)',
        'Liste des problèmes groupés par type avec nombre de produits affectés',
        'Actions correctives suggérées en français',
        'Identification des problèmes prioritaires à résoudre'
      ]
    },
    {
      id: 'sea-seo-overlap',
      name: 'Cannibalisation SEA/SEO',
      description: 'Détecter les termes de recherche présents en SEA et en position 1 SEO',
      icon: <Layers size={24} />,
      status: 'GOOGLE ADS + GSC',
      badge: 'NEW',
      features: [
        'Cross-référencement des termes SEA et SEO',
        'Détection des termes en position 1 organique',
        'Calcul des économies publicitaires potentielles',
        'Priorisation par coût SEA et position SEO',
        'Connexion Google Search Console intégrée'
      ]
    },
    {
      id: 'feed-optimizer',
      name: 'Optimiseur de Flux',
      description: 'Analyser et optimiser les attributs de vos produits Merchant Center avec l\'IA',
      icon: <Store size={24} />,
      status: 'MERCHANT API',
      badge: 'NEW',
      features: [
        'Analyse automatique du flux avec détection des problèmes',
        'Suggestions d\'amélioration générées par IA (titres, descriptions, attributs)',
        'Édition manuelle des suggestions avant validation',
        'Validation individuelle ou en masse',
        'Génération de flux XML supplémentaire pour Merchant Center'
      ]
    },
    {
      id: 'competitor-ads',
      name: 'Veille concurrentielle Social Ads',
      description: 'Surveillez les publicités de vos concurrents sur Meta (Facebook/Instagram)',
      icon: <Sparkles size={24} />,
      status: 'META ADS',
      badge: 'NEW',
      features: [
        'Suivi des pubs actives et stoppées des concurrents',
        'Détection automatique des nouvelles créas',
        'Analyse de la longévité (proxy de performance)',
        'Insights IA sur les stratégies concurrentes',
        'Répartition par plateforme (Facebook, Instagram)'
      ]
    },
    {
      id: 'serp-tracking',
      name: 'SERP Tracking',
      description: 'Suivez vos positions SEO et GEO (AI Overviews) sur Google dans 195+ pays',
      icon: <Globe size={24} />,
      status: 'DATAFORSEO',
      badge: 'NEW',
      features: [
        'Suivi des positions organiques sur Google',
        'Détection des AI Overviews (GEO)',
        'Analyse des SERP features (featured snippets, local pack, etc.)',
        '195+ pays et régions disponibles',
        'Fréquence configurable (quotidien, hebdomadaire, etc.)',
        'Historique des positions avec graphiques'
      ]
    },
    {
      id: 'ai-visibility',
      name: 'AI Visibility',
      description: 'Suivez votre visibilité dans ChatGPT, Claude, Gemini, Perplexity',
      icon: <Bot size={24} />,
      status: 'DATAFORSEO',
      badge: 'NEW',
      features: [
        'Détection des mentions dans les réponses LLM',
        'Suivi des citations (sources)',
        'ChatGPT, Claude, Gemini, Perplexity',
        'Historique et tendances',
        'Fréquence configurable'
      ]
    },
    {
      id: 'backlinks',
      name: 'Backlinks',
      description: 'Analysez le profil de liens entrants d\'un domaine : autorité, sources, ancres',
      icon: <Link2 size={24} />,
      status: 'DATAFORSEO',
      badge: 'NEW',
      features: [
        'Autorité du domaine sur 100, volume de liens et de domaines référents',
        'Liens gagnés et perdus mois par mois depuis 2019',
        'Liste nominative des liens apparus et des liens disparus',
        'Détail des liens, des domaines référents et des ancres',
        'Score de spam et liens cassés',
        'Concurrents partageant vos sources de liens',
        'Chaque analyse est conservée et se compare aux précédentes'
      ]
    },
    {
      id: 'diagnostic-seo',
      name: 'Diagnostic SEO',
      description: 'Trente-trois contrôles sur une propriété Search Console : indexation, technique, contenu, requêtes, performance',
      icon: <Search size={24} />,
      status: 'SEARCH CONSOLE',
      badge: 'NEW',
      features: [
        'Pages qui reçoivent des impressions sans être indexées',
        'CTR très inférieur à la position, et pages en deuxième page',
        'Cannibalisation : plusieurs de vos pages sur une même requête',
        'Chute de clics face à la période comparable précédente',
        'Titres, méta descriptions, H1, canoniques, hreflang, robots.txt',
        'Core Web Vitals mesurés sur les visites réelles',
        'Chaque constat classé par le trafic qu\'il met en jeu',
        'Aucun coût : Search Console et PageSpeed Insights sont gratuits'
      ]
    },
    {
      id: 'diagnostic',
      name: 'Diagnostic Google Ads',
      description: 'Scanner automatique de vos comptes Google Ads pour détecter les problèmes',
      icon: <Activity size={24} />,
      status: 'GOOGLE ADS',
      features: [
        'Analyse automatique des comptes Google Ads',
        '26+ vérifications (structure, enchères, ciblage, etc.)',
        'Score de santé global par compte',
        'Recommandations d\'actions prioritaires',
        'Historique des scans et évolution',
        'Notifications email configurables'
      ]
    },
  ];

  /**
   * La catégorie de chaque outil vient de `constants/toolCategories`, et non plus d'un champ
   * recopié ici : elle était aussi écrite à la main dans la barre latérale, et les deux listes
   * n'avaient rien qui les tienne ensemble.
   *
   * Un outil absent du module ressort en « Autres » plutôt que de disparaître : le catalogue
   * n'affiche que les catégories connues, et une catégorie inconnue l'aurait tu sans bruit.
   */
  const toolsClasses = tools.map(tool => ({
    ...tool,
    category: categorieDeLOutil(tool.id) || 'Autres',
    sousCategorie: sousCategorieDeLOutil(tool.id)
  }));

  // Refus par défaut pour un accès client : il ne voit que les outils que son agence lui
  // a ouverts. `null` pour tous les autres rôles, qui gardent le catalogue complet.
  const outilsAutorises = user?.role === 'CLIENT' ? clientTools : null;
  const visibleTools = outilsAutorises
    ? toolsClasses.filter(tool => outilsAutorises.includes(tool.id))
    : toolsClasses;

  // Ordre des catégories pour l'affichage, celui de la barre latérale. « Autres » ferme la
  // marche : elle ne contient que ce qu'on a oublié de ranger.
  const categoryOrder = [...ORDRE_CATEGORIES, 'Autres'];
  const uniqueCategories = [...new Set(visibleTools.map(tool => tool.category))];
  const toutesLesCategories = categoryOrder.filter(cat => uniqueCategories.includes(cat));

  /**
   * Une catégorie cadrée depuis la barre latérale ne laisse qu'elle à l'écran. Si elle a disparu
   * entre-temps — un outil retiré à un compte client —, on remontre tout plutôt qu'une page vide.
   *
   * Le cadrage accepte les deux niveaux : cliquer « Shopping » réduit la page à ces trois outils,
   * sous la section de leur catégorie. Le libellé reçu ne dit pas son rang, d'où le test.
   */
  const sousCategorieCadree = categorieCadree && estUneSousCategorie(categorieCadree)
    ? categorieCadree
    : null;

  const categorieRetenue = sousCategorieCadree ? categorieParenteDe(sousCategorieCadree) : categorieCadree;

  /**
   * Le cadrage n'est effectif que si la catégorie visée existe encore. Le distinguer du simple
   * `categorieCadree` importe : c'est lui qui décide de taire les titres, et les taire sur un
   * cadrage retombé laisserait le catalogue entier sans aucun intertitre.
   */
  const cadrageActif = Boolean(categorieRetenue) && toutesLesCategories.includes(categorieRetenue);

  const categories = cadrageActif ? [categorieRetenue] : toutesLesCategories;

  const handleToolClick = (tool) => {
    // Accès direct à l'outil au lieu d'ouvrir le modal
    if (tool.status !== 'À venir') {
      setActiveToolWithHistory(tool.id);
    }
  };

  const handleInfoClick = (tool, event) => {
    event.stopPropagation();
    setSelectedTool(tool);
  };

  const closeModal = () => {
    setSelectedTool(null);
  };

  const handleUseTool = (toolId) => {
    setActiveToolWithHistory(toolId);
    setSelectedTool(null);
  };

  /**
   * Retour au catalogue entier.
   *
   * Le cadrage de catégorie est annulé au passage : « Outils » désigne tous les outils, et s'y
   * rendre en conservant un filtre aurait rendu le clic sans effet visible quand aucun outil
   * n'est ouvert — c'était le cas, le lien n'étant alors même pas cliquable.
   */
  const handleBackToTools = () => {
    setActiveToolWithHistory(null);
    setCategorieCadree(null);
  };

  // Mapping des sous-outils vers leur hub parent
  const hubMapping = {
    'negative-keywords': { hubId: 'negative-keywords-hub', hubName: 'Termes de Recherche à Exclure', subName: 'Google Ads' },
    'microsoft-negative-keywords': { hubId: 'negative-keywords-hub', hubName: 'Termes de Recherche à Exclure', subName: 'Microsoft Ads' }
  };

  // Récupérer le nom de l'outil actif
  const activeToolName = activeTool
    ? tools.find(t => t.id === activeTool)?.name || activeTool
    : null;

  /**
   * Fil d'Ariane : `[Client] › Outils › Catégorie › [Hub ›] Outil`.
   *
   * La catégorie y figure **aussi quand un outil est ouvert**, et pas seulement quand le
   * catalogue est cadré sur elle. Elle ne s'affichait qu'au cadrage : ouvrir un outil depuis la
   * grille donnait « Outils › Termes de Recherche à Exclure », sans jamais dire de quel levier
   * il relève, et sans offrir le chemin de retour vers ses voisins.
   *
   * Chaque maillon qui désigne une destination est cliquable. Le dernier ne l'est pas : il dit
   * la position courante, et un dernier maillon qui réagit au clic laisse croire qu'il mène
   * ailleurs. Le nom du client non plus : c'est le contexte de lecture, comme dans les cinq
   * autres fils d'Ariane de l'application, où il n'a jamais été une destination.
   */
  const renderBreadcrumb = () => {
    const hubInfo = hubMapping[activeTool];
    // Les maillons que l'outil ouvert ajoute après son propre nom — voir `filDeLOutil`.
    const suite = filDeLOutil;
    // Le cadrage porte sur un libellé seul, qui peut désigner l'un ou l'autre niveau.
    const cadreSurUneSousCategorie = categorieCadree && estUneSousCategorie(categorieCadree);
    // Un outil ouvert impose sa catégorie, quel que soit le cadrage : c'est celle de l'outil
    // qu'on lit, pas celle par laquelle on est passé.
    const categorie = activeTool
      ? categorieDeLOutil(activeTool)
      : (cadreSurUneSousCategorie ? categorieParenteDe(categorieCadree) : categorieCadree);
    const sousCategorie = activeTool
      ? sousCategorieDeLOutil(activeTool)
      : (cadreSurUneSousCategorie ? categorieCadree : null);

    // La position courante est le dernier maillon affiché : la sous-catégorie quand elle existe
    // et qu'aucun outil n'est ouvert, la catégorie sinon.
    const categorieEstLaPosition = Boolean(categorie) && !activeTool && !sousCategorie;
    const sousCategorieEstLaPosition = Boolean(sousCategorie) && !activeTool;

    const cadrerSur = (libelle) => {
      setActiveToolWithHistory(null);
      setCategorieCadree(libelle);
    };

    const maillon = (contenu, { actif = false, action, titre } = {}) => (
      <span
        className={`breadcrumb-nav-item ${actif ? 'active' : ''}`}
        onClick={action}
        style={action ? { cursor: 'pointer' } : undefined}
        title={titre}
      >
        {contenu}
      </span>
    );

    const separateur = (cle) => <ChevronRight key={cle} size={14} className="breadcrumb-separator" />;

    return (
      <div className="app-breadcrumb">
        {selectedClientName && (
          <>
            {maillon(selectedClientName)}
            {separateur('client')}
          </>
        )}
        {/* « Outils » est cliquable dès qu'on n'est pas déjà sur le catalogue entier — un outil
            ouvert, mais aussi une simple catégorie cadrée. C'est le segment qui ramène en arrière,
            et un fil d'Ariane dont le premier maillon ne mène nulle part n'en est pas un. */}
        {maillon(
          <><Wrench size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Outils</>,
          {
            actif: !activeTool && !categorieCadree,
            action: activeTool || categorieCadree ? handleBackToTools : undefined,
            titre: activeTool || categorieCadree ? 'Voir tous les outils' : undefined
          }
        )}
        {categorie && (
          <>
            {separateur('categorie')}
            {maillon(categorie, {
              actif: categorieEstLaPosition,
              // Cliquer la catégorie referme l'outil et cadre le catalogue sur elle : c'est le
              // moyen de passer à un outil voisin sans repasser par le catalogue entier.
              action: categorieEstLaPosition ? undefined : () => cadrerSur(categorie),
              titre: categorieEstLaPosition ? undefined : `Voir les outils ${categorie}`
            })}
          </>
        )}
        {sousCategorie && (
          <>
            {separateur('sous-categorie')}
            {maillon(sousCategorie, {
              actif: sousCategorieEstLaPosition,
              action: sousCategorieEstLaPosition ? undefined : () => cadrerSur(sousCategorie),
              titre: sousCategorieEstLaPosition ? undefined : `Voir les outils ${sousCategorie}`
            })}
          </>
        )}
        {hubInfo ? (
          <>
            {separateur('hub')}
            {maillon(hubInfo.hubName, { action: () => setActiveToolWithHistory(hubInfo.hubId) })}
            {separateur('sous-outil')}
            {maillon(hubInfo.subName, { actif: true })}
          </>
        ) : activeToolName && (
          <>
            {separateur('outil')}
            {maillon(activeToolName, suite.length > 0 ? {} : { actif: true })}
          </>
        )}
        {/* La suite, quand l'outil ouvert navigue à l'intérieur de lui-même : le compte choisi
            dans le Diagnostic, puis l'écran des contrôles. Le dernier maillon est la position
            courante ; les précédents ramènent en arrière par l'action qu'ils portent. */}
        {suite.map((etape, rang) => (
          <React.Fragment key={etape.cle}>
            {separateur(`suite-${etape.cle}`)}
            {maillon(etape.libelle, rang === suite.length - 1
              ? { actif: true }
              : { action: etape.action, titre: etape.titre })}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // `activeTool` est posé par quatre chemins qui ne passent pas par la grille : l'URL au
  // montage, le bouton précédent, le localStorage et l'événement de la sidebar. Un seul
  // verrou ici ferme les quatre — filtrer la grille n'aurait rien empêché.
  if (activeTool && outilsAutorises && !outilsAutorises.includes(activeTool)) {
    return (
      <div className="tools-view">
        {renderBreadcrumb()}
        <div className="empty-state" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
          <p>Cet outil ne vous a pas été ouvert.</p>
        </div>
      </div>
    );
  }

  // Si un outil est actif, afficher l'outil
  if (activeTool === 'product-labeling') {
    return (
      <div className="tools-view">
        {renderBreadcrumb()}
        {/* Même contrat que l'Optimiseur de flux et la veille : consultation seule pour un client. */}
        <ProductLabelingTool
          token={token}
          selectedClientId={selectedClientId}
          updateUrlWithAccount={updateUrlWithAccount}
          globalConnectors={globalConnectors}
          setCurrentView={setCurrentView}
          readOnly={user?.role === 'CLIENT'}
        />
      </div>
    );
  }

  if (activeTool === 'keyword-opportunities') {
    return (
      <div className="tools-view">
        {renderBreadcrumb()}
        <KeywordOpportunitiesTool token={token} selectedClientId={selectedClientId} updateUrlWithAccount={updateUrlWithAccount} />
      </div>
    );
  }

  if (activeTool === 'keyword-planner') {
    return (
      <div className="tools-view">
        {renderBreadcrumb()}
        <KeywordPlannerTool token={token} selectedClientId={selectedClientId} updateUrlWithAccount={updateUrlWithAccount} />
      </div>
    );
  }

  if (activeTool === 'negative-keywords-hub') {
    return (
      <div className="tools-view">
        {renderBreadcrumb()}
        <div className="tool-hub">
          <div className="tool-hub-header">
            <Ban size={32} />
            <h1>Termes de Recherche à Exclure</h1>
            <p>Identifier les termes de recherche qui gaspillent votre budget</p>
          </div>
          <div className="tool-hub-grid">
            <div
              className="tool-hub-card"
              onClick={() => setActiveToolWithHistory('negative-keywords')}
            >
              <img src={googleAdsLogo} alt="Google Ads" className="tool-hub-logo" />
              <h3>Google Ads</h3>
              <p>Analyser les termes de recherche de vos campagnes Search et Performance Max</p>
            </div>
            <div
              className="tool-hub-card"
              onClick={() => setActiveToolWithHistory('microsoft-negative-keywords')}
            >
              <img src={microsoftAdsLogo} alt="Microsoft Ads" className="tool-hub-logo" />
              <h3>Microsoft Ads</h3>
              <p>Analyser les termes de recherche de vos campagnes Microsoft Advertising</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (activeTool === 'negative-keywords') {
    return (
      <div className="tools-view">
        {renderBreadcrumb()}
        <NegativeKeywordsTool token={token} selectedClientId={selectedClientId} updateUrlWithAccount={updateUrlWithAccount} />
      </div>
    );
  }

  if (activeTool === 'microsoft-negative-keywords') {
    return (
      <div className="tools-view">
        {renderBreadcrumb()}
        <MicrosoftNegativeKeywordsTool token={token} selectedClientId={selectedClientId} updateUrlWithAccount={updateUrlWithAccount} />
      </div>
    );
  }

  if (activeTool === 'bid-optimizer') {
    return (
      <div className="tools-view">
        {renderBreadcrumb()}
        <BidOptimizerTool token={token} selectedClientId={selectedClientId} updateUrlWithAccount={updateUrlWithAccount} />
      </div>
    );
  }

  if (activeTool === 'device-bid-adjustments') {
    return (
      <div className="tools-view">
        {renderBreadcrumb()}
        <DeviceBidAdjustmentsTool token={token} selectedClientId={selectedClientId} updateUrlWithAccount={updateUrlWithAccount} />
      </div>
    );
  }

  if (activeTool === 'feed-diagnostics') {
    return (
      <div className="tools-view">
        {renderBreadcrumb()}
        <FeedDiagnosticsTool token={token} selectedClientId={selectedClientId} />
      </div>
    );
  }

  if (activeTool === 'sea-seo-overlap') {
    return (
      <div className="tools-view">
        {renderBreadcrumb()}
        <SeaSeoOverlapTool
          token={token}
          selectedClientId={selectedClientId}
          updateUrlWithAccount={updateUrlWithAccount}
          setCurrentView={setCurrentView}
          setOpenSearchConsoleOnLibraryLoad={setOpenSearchConsoleOnLibraryLoad}
        />
      </div>
    );
  }

  if (activeTool === 'feed-optimizer') {
    return (
      <div className="tools-view">
        {renderBreadcrumb()}
        {/* Même contrat que la veille concurrentielle : consultation seule pour un client. */}
        <FeedOptimizerTool
          token={token}
          selectedClientId={selectedClientId}
          readOnly={user?.role === 'CLIENT'}
        />
      </div>
    );
  }

  if (activeTool === 'competitor-ads') {
    return (
      <div className="tools-view">
        {renderBreadcrumb()}
        <CompetitorAdWatchTool
          token={token}
          selectedClientId={selectedClientId}
          readOnly={user?.role === 'CLIENT'}
        />
      </div>
    );
  }

  if (activeTool === 'serp-tracking') {
    return (
      <div className="tools-view">
        {renderBreadcrumb()}
        <SerpTrackingTool token={token} selectedClientId={selectedClientId} />
      </div>
    );
  }

  if (activeTool === 'ai-visibility') {
    return (
      <div className="tools-view">
        {renderBreadcrumb()}
        <AiVisibilityTool token={token} selectedClientId={selectedClientId} />
      </div>
    );
  }

  if (activeTool === 'backlinks') {
    return (
      <div className="tools-view">
        {renderBreadcrumb()}
        <BacklinksTool token={token} selectedClientId={selectedClientId} />
      </div>
    );
  }

  if (activeTool === 'diagnostic-seo') {
    return (
      <div className="tools-view">
        {renderBreadcrumb()}
        <DiagnosticSeoView token={token} selectedClientId={selectedClientId} />
      </div>
    );
  }

  if (activeTool === 'diagnostic') {
    return (
      <div className="tools-view">
        {renderBreadcrumb()}
        <DiagnosticView
          token={token}
          user={user}
          selectedClientId={selectedClientId}
          userClients={userClients}
          hideBreadcrumb={true}
          isToolsView={true}
          onFilChange={setFilDeLOutil}
        />
      </div>
    );
  }

  return (
    <div className="tools-view">
      {renderBreadcrumb()}

      <div className="tools-grid">
        {categories.map(category => {
          const outilsDeLaCategorie = visibleTools.filter(tool => (
            tool.category === category
            && !tool.hidden
            // Un cadrage sur une sous-catégorie ne garde qu'elle, la section restant celle de
            // la catégorie parente : « Shopping » seul ne dirait pas de quel levier il relève.
            && (!sousCategorieCadree || tool.sousCategorie === sousCategorieCadree)
          ));

          const carte = (tool) => (
            <div
              key={tool.id}
              className="tool-card"
              onClick={() => handleToolClick(tool)}
            >
              <div className="tool-icon">
                {tool.icon}
              </div>
              <div className="tool-content">
                <h3 className="tool-name">{tool.name}</h3>
                <p className="tool-description">{tool.description}</p>
              </div>
              <div className="tool-card-right">
                <button
                  className="info-btn"
                  onClick={(e) => handleInfoClick(tool, e)}
                  title="Plus d'informations"
                >
                  ?
                </button>
                <LogoDeLOutil statut={tool.status} />
              </div>
            </div>
          );

          return (
            <div key={category} className="tools-category">
              {/* Entré par la barre latérale, le titre de section ne dirait que ce que le fil
                  d'Ariane vient de dire, au-dessus d'une page qui ne contient que lui. On ne le
                  tait qu'au cadrage : hors cadrage, il sépare les quatre sections. */}
              {!cadrageActif && <h2 className="category-title">{category}</h2>}
              {/* Même découpage que la barre latérale, par la même fonction : les deux écrans
                  présenteraient sinon deux classements pour une seule structure. */}
              {grouperParSousCategorie(outilsDeLaCategorie).map(groupe => (
                <div key={groupe.titre || '—'}>
                  {/* Même raison pour l'intertitre, mais seulement quand le cadrage porte
                      précisément sur lui. Cadré sur SEA, le bloc Shopping garde le sien : il
                      délimite un sous-ensemble au milieu des autres outils du levier. */}
                  {groupe.titre && groupe.titre !== sousCategorieCadree && (
                    <h3 className="subcategory-title">{groupe.titre}</h3>
                  )}
                  <div className="category-tools">
                    {groupe.outils.map(carte)}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Modal pour les détails de l'outil - rendu via Portal pour être au-dessus du header */}
      {selectedTool && ReactDOM.createPortal(
        <div className="tool-modal-overlay" onClick={closeModal}>
          <div className="tool-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                {selectedTool.icon}
                <h2>{selectedTool.name}</h2>
              </div>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>

            <p className="modal-description">{selectedTool.description}</p>

            <div className="modal-features">
              <h3>Fonctionnalités :</h3>
              <ul>
                {selectedTool.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>

            {selectedTool.status === 'À venir' && (
              <div className="modal-status">
                <span className={`status-badge coming-soon`}>
                  {selectedTool.status}
                </span>
                <p className="coming-soon-text">
                  Cet outil sera disponible dans une prochaine version.
                  Restez connecté pour les mises à jour !
                </p>
              </div>
            )}

            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeModal}>
                Fermer
              </button>
              {selectedTool.status !== 'À venir' && (
                <button
                  className="btn-primary"
                  onClick={() => handleUseTool(selectedTool.id)}
                >
                  Utiliser l'outil
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ToolsView;